// Aviso "la valuación quedó desactualizada" + botón "Recalcular apreciación".
// internal/specs/recalcular-apreciacion.md. Aparece cuando los valores
// referenciales (Tabla A/B) cambiaron después del último devengo de apreciación.
// El botón recalcula los meses NO sellados con los valores actuales (los meses
// sellados no se tocan). Solo owner/admin pueden recalcular; viewer ve el aviso.

import { APPRECIATION_ENABLED } from "@/common/lib/appreciationEnabled";
import {
  useAppreciationStale,
  useRecomputeAppreciation,
} from "@/features/journal/api/referenceTablesApi";
import { RefreshCw, TrendingUp } from "lucide-react";
import { toast } from "sonner";

export function AppreciationStaleBanner({
  accountId,
  canRecompute,
  className,
}: {
  accountId: string;
  canRecompute: boolean;
  className?: string;
}) {
  const staleQuery = useAppreciationStale(accountId);
  const recompute = useRecomputeAppreciation(accountId);

  // Apreciación apagada en v1 (apreciacion-apagada-v1.md): gate explícito en
  // la UI (no depende solo del stale=false del backend). Un solo punto que
  // cubre Depreciación, Valores referenciales y cualquier uso futuro.
  // Revertir = flipar APPRECIATION_ENABLED en common/lib/appreciationEnabled.ts.
  if (!APPRECIATION_ENABLED) return null;

  if (!staleQuery.data?.stale) return null;

  async function handleRecompute() {
    try {
      const { result } = await recompute.mutateAsync();
      toast.success(
        result.monthsRecomputed > 0
          ? `Apreciación recalculada (${result.monthsRecomputed} ${
              result.monthsRecomputed === 1 ? "mes" : "meses"
            }).`
          : "Apreciación al día.",
      );
    } catch {
      toast.error("No se pudo recalcular. Intentá de nuevo.");
    }
  }

  return (
    <output
      className={`flex flex-col gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between${
        className ? ` ${className}` : ""
      }`}
    >
      <div className="flex items-start gap-2 text-amber-900">
        <TrendingUp aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
        <p className="text-sm">
          Cambiaste los valores de mercado. La valuación de tu hato quedó desactualizada.
        </p>
      </div>
      {canRecompute && (
        <button
          type="button"
          onClick={handleRecompute}
          disabled={recompute.isPending}
          className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-lg bg-app-ink-1 px-4 font-medium text-sm text-white hover:bg-[#1e211d] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RefreshCw
            aria-hidden
            className={`h-4 w-4 ${recompute.isPending ? "animate-spin" : ""}`}
          />
          {recompute.isPending ? "Recalculando…" : "Recalcular apreciación"}
        </button>
      )}
    </output>
  );
}
